import {
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
  Tooltip,
} from '@ads/components-react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  ListBulletIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { DataTable } from '@/shared/ui/organisms/DataTable';
import { decodeHtmlEntities } from '@/shared/utils/decodeHtmlEntities';
import type { Question, QuestionStatus } from '../../../hooks/useQuestionLibrary';
import { normalizeHtml } from '../../../utils';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do componente QuestionTable */
export interface QuestionTableProps {
  /** Lista de questões a exibir */
  readonly data: Question[];
  /** Total de questões (paginação server-side) */
  readonly totalCount: number;
  /** Página atual (1-indexed) */
  readonly currentPage: number;
  /** Total de páginas */
  readonly totalPages: number;
  /** Estado de carregamento — exibe skeleton quando true */
  readonly isLoading: boolean;
  /** Callback ao mudar a página */
  readonly onPageChange: (page: number) => void;
  /** Callback ao clicar em "Visualizar" */
  readonly onPreview?: (id: string) => void;
  /** Callback ao clicar em "Editar" */
  readonly onEdit?: (id: string) => void;
  /** Callback ao clicar em "Excluir" */
  readonly onDelete?: (id: string) => void;
  /** Callback ao clicar em "Histórico" */
  readonly onHistory?: (id: string) => void;
}

// ─── Column helper ────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper<Question>();

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusTagMap: Record<
  QuestionStatus,
  { status: 'positive' | 'helper' | 'negative' | 'default' }
> = {
  Aprovado: { status: 'positive' },
  Pendente: { status: 'helper' },
  EmRevisao: { status: 'default' },
  Rejeitado: { status: 'negative' },
};

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Tabela de questões da Biblioteca de Questões.
 *
 * Exibe colunas de domínio (texto, disciplina, autor, idioma, status, ativo)
 * com ações por linha (gabarito, visualizar, histórico, editar, excluir).
 *
 * @example
 * ```tsx
 * <QuestionTable
 *   data={questions}
 *   totalCount={148}
 *   currentPage={1}
 *   totalPages={8}
 *   isLoading={false}
 *   onPageChange={page => updateFilters({ page })}
 *   onEdit={id => navigate({ to: '/question-library/$id/edit', params: { id } })}
 * />
 * ```
 */
export function QuestionTable({
  data,
  totalCount,
  currentPage,
  totalPages,
  isLoading,
  onPageChange,
  onPreview,
  onEdit,
  onDelete,
  onHistory,
}: QuestionTableProps) {
  const { t } = useTranslation('questionLibrary');

  const columns = useMemo(
    () =>
      [
        // Questão
        columnHelper.accessor('text', {
          header: t('table.columns.question'),
          cell: info => {
            const preview = normalizeHtml(info.getValue());
            return (
              <Tooltip align="start" title={decodeHtmlEntities(preview)}>
                <span className="line-clamp-2 max-w-[280px] text-ctx-content-title ts-paragraph-xxs">
                  {decodeHtmlEntities(preview) || '-'}
                </span>
              </Tooltip>
            );
          },
        }),

        // Disciplina
        columnHelper.accessor('discipline', {
          header: t('table.columns.discipline'),
          cell: info =>
            info.getValue() ? (
              <Tooltip align="start" title={info.getValue()}>
                <span className="line-clamp-1 text-ctx-content-base ts-paragraph-xxs">
                  {info.getValue()}
                </span>
              </Tooltip>
            ) : (
              <div className="text-ctx-content-base ts-paragraph-xxs text-center">-</div>
            ),
        }),

        // Autor
        columnHelper.accessor('author', {
          header: t('table.columns.author'),
          cell: info => (
            <Tooltip title={info.getValue()}>
              <span className="line-clamp-1 text-ctx-content-base ts-paragraph-xxs">
                {info.getValue()}
              </span>
            </Tooltip>
          ),
        }),

        // Idioma
        columnHelper.accessor('language', {
          header: t('table.columns.language'),
          cell: info => (
            <span className="text-ctx-content-base ts-paragraph-xxs">
              {t(`language.${info.getValue()}`)}
            </span>
          ),
        }),

        // Status
        columnHelper.accessor('status', {
          header: t('table.columns.status'),
          cell: info => {
            const { status } = statusTagMap[info.getValue()];
            return (
              <Tag
                status={status}
                label={t(`status.${info.getValue()}`)}
                size="md"
                custom={{ text: 'text-nowrap' }}
              />
            );
          },
        }),

        // Ativo
        columnHelper.accessor('isActive', {
          header: t('table.columns.active'),
          cell: info => (
            <div className="flex items-center justify-center">
              {info.getValue() ? (
                <CheckCircleIcon
                  className="size-xxs text-base-success-500"
                  aria-label={t('table.aria.active')}
                />
              ) : (
                <XCircleIcon
                  className="size-xxs text-base-danger-500"
                  aria-label={t('table.aria.inactive')}
                />
              )}
            </div>
          ),
        }),

        // Ações
        columnHelper.display({
          id: 'actions',
          header: () => null,
          cell: ({ row }) => {
            const id = row.original.id;
            return (
              <Dropdown
                trigger={
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    ariaLabel={t('actions.edit')}
                    hierarchy="tertiary"
                    size="md"
                  />
                }
                align="end"
              >
                <DropdownItem onClick={() => onPreview?.(id)}>
                  <DropdownItemContent leadingIcon={EyeIcon}>
                    {t('actions.preview')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onHistory?.(id)}>
                  <DropdownItemContent leadingIcon={ListBulletIcon}>
                    {t('actions.history')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onEdit?.(id)}>
                  <DropdownItemContent leadingIcon={PencilSquareIcon}>
                    {t('actions.edit')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem status="destructive" onClick={() => onDelete?.(id)}>
                  <DropdownItemContent leadingIcon={TrashIcon}>
                    {t('actions.delete')}
                  </DropdownItemContent>
                </DropdownItem>
              </Dropdown>
            );
          },
        }),
      ] as ColumnDef<Question, unknown>[],
    [t, onDelete, onEdit, onHistory, onPreview],
  );

  if (isLoading) {
    return <QuestionTableSkeleton />;
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      emptyStateMessage={t('table.empty')}
      onPageChange={onPageChange}
      skeletonRowCount={10}
    />
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function QuestionTableSkeleton() {
  return (
    <div className="w-full overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
      {/* Header skeleton */}
      <div className="bg-ctx-layout-body px-mini py-xxs">
        <div className="grid grid-cols-7 gap-xs">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-micro w-3/4" />
          ))}
        </div>
      </div>

      {/* Row skeletons */}
      <div className="divide-y divide-ctx-layout-border bg-ctx-layout-surface">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="grid grid-cols-7 gap-xs px-mini py-xxs">
            <Skeleton className="col-span-1 h-mini w-full" />
            <Skeleton className="col-span-1 h-mini w-4/5" />
            <Skeleton className="col-span-1 h-mini w-3/4" />
            <Skeleton className="col-span-1 h-mini w-1/2" />
            <Skeleton className="w-16 col-span-1 h-mini rounded-pill" />
            <Skeleton className="col-span-1 h-mini w-mini rounded-circular" />
            <div className="col-span-1 flex justify-end gap-quark">
              <Skeleton className="size-xs rounded-sm" />
              <Skeleton className="size-xs rounded-sm" />
              <Skeleton className="size-xs rounded-sm" />
            </div>
          </div>
        ))}
      </div>

      {/* Footer skeleton */}
      <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-mini py-micro">
        <Skeleton className="w-[10rem] h-micro" />
        <div className="flex gap-nano">
          <Skeleton className="w-[6rem] h-sm rounded-sm" />
          <Skeleton className="w-[4rem] h-sm rounded-sm" />
          <Skeleton className="w-[6rem] h-sm rounded-sm" />
        </div>
      </div>
    </div>
  );
}
